import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../Store";

export interface ValidationError {
    message: string;
    nodeId?: string;
}

export interface ValidationState {
    warnings: Record<string, string[]>;
    errors: Record<string, ValidationError[]>;
}

const initialState: ValidationState = {
    warnings: {},
    errors: {},
};

// Helper to convert ValidationError[] to string[] for backward compatibility
export const getErrorMessages = (errors: ValidationError[]): string[] => {
    return errors.map(e => e.message);
};

export const validationSlice = createSlice({
    name: "validation",
    initialState,
    reducers: {
        InitializeWarnings: (
            state,
            action: PayloadAction<Record<string, string[]>>
        ) => {
            state.warnings = action.payload;
        },
        InitializeErrors: (
            state,
            action: PayloadAction<Record<string, ValidationError[]>>
        ) => {
            state.errors = action.payload;
        },
        addInteractionWarning: (
            state,
            action: PayloadAction<{ interactionId: string; warnings: string[] }>
        ) => {
            state.warnings[action.payload.interactionId] = action.payload.warnings;
        },
        updateInteractionError: (
            state,
            action: PayloadAction<{ interactionId: string; errors: ValidationError[] | string[] | (ValidationError | string)[] }>
        ) => {
            // Convert all errors to ValidationError format
            const errors = action.payload.errors.map(e => 
                typeof e === 'string' ? { message: e } : e
            );
            state.errors[action.payload.interactionId] = errors;
        },
        removeWarningsAndErrorsForInteraction: (state, action: PayloadAction<{ interactionId: string }>) => {
            delete state.warnings[action.payload.interactionId];
            delete state.errors[action.payload.interactionId];
        },
        removeWarningsAndErrorsForInteractions: (state, action: PayloadAction<{ interactionIds: string[] }>) => {
            action.payload.interactionIds.forEach(interactionId => {
                delete state.warnings[interactionId];
                delete state.errors[interactionId];
            });
        }
            
    },
});

export const {
    InitializeWarnings,
    InitializeErrors,
    addInteractionWarning,
    updateInteractionError,
    removeWarningsAndErrorsForInteraction,
    removeWarningsAndErrorsForInteractions
} = validationSlice.actions;

export const selectWarnings = (state: RootState) => state.validation.warnings;
export const selectErrors = (state: RootState) => state.validation.errors;

export const selectWarningsForInteraction = createSelector(
    [
        (state: RootState) => state.validation.warnings,
        (_state: RootState, interactionId: string) => interactionId,
    ],
    (warnings, interactionId) => warnings[interactionId] ?? []
);

export const selectErrorsForInteraction = createSelector(
    [
        (state: RootState) => state.validation.errors,
        (_state: RootState, interactionId: string) => interactionId,
    ],
    (errors, interactionId) => errors[interactionId] ?? []
);

export const selectTotalErrorCount = createSelector(
    [(state: RootState) => state.validation.errors],
    (errors) =>
        Object.values(errors).reduce((total, list) => total + list.length, 0)
);

export const selectTotalWarningCount = createSelector(
    [(state: RootState) => state.validation.warnings],
    (warnings) =>
        Object.values(warnings).reduce((total, list) => total + list.length, 0)
);

export const selectErrorMessagesForInteraction = createSelector(
    [
        (state: RootState) => state.validation.errors,
        (_state: RootState, interactionId: string) => interactionId,
    ],
    (errors, interactionId) => {
        const errorList = errors[interactionId] ?? [];
        return errorList.map(e => typeof e === 'string' ? e : e.message);
    }
);

export default validationSlice.reducer;
